// ./src/contexts/ExpenseSchedulesContext.tsx
//
// Thin context wrapper around the useExpenseSchedules hook.
//
// Usage:
//   <ExpenseSchedulesProvider autoLoad pageSize={50}>
//     <MyComponent />          {/* reads via useExpenseSchedulesContext() */}
//   </ExpenseSchedulesProvider>
//
// Two accessors are exported:
//   useExpenseSchedulesContext()         — throws if no provider (for components that *require* it)
//   useOptionalExpenseSchedulesContext() — returns null (for components that can work with or without)

import React, { createContext, useContext } from "react";
import {
  useExpenseSchedules,
  UseExpenseSchedulesOptions,
  UseExpenseSchedulesReturn,
} from "@/hooks/useExpenseSchedules";

// ============================================
// Context
// ============================================

const ExpenseSchedulesContext = createContext<UseExpenseSchedulesReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface ExpenseSchedulesProviderProps extends UseExpenseSchedulesOptions {
  children: React.ReactNode;
}

/**
 * Wraps children with a shared ExpenseSchedules state.
 * Each provider instance creates its own independent hook,
 * so two providers on different pages won't interfere.
 */
export const ExpenseSchedulesProvider: React.FC<ExpenseSchedulesProviderProps> = ({
  children,
  ...options
}) => {
  const value = useExpenseSchedules(options);

  return (
    <ExpenseSchedulesContext.Provider value={value}>
      {children}
    </ExpenseSchedulesContext.Provider>
  );
};

// ============================================
// Strict accessor — throws when no provider
// ============================================

/**
 * Returns the ExpenseSchedules context value.
 * Throws if called outside a <ExpenseSchedulesProvider>.
 */
export const useExpenseSchedulesContext = (): UseExpenseSchedulesReturn => {
  const context = useContext(ExpenseSchedulesContext);

  if (!context) {
    throw new Error(
      "useExpenseSchedulesContext must be used within an <ExpenseSchedulesProvider>. " +
      "Wrap your component tree with <ExpenseSchedulesProvider> to fix this."
    );
  }

  return context;
};

// ============================================
// Safe accessor — returns null when no provider
// ============================================

/**
 * Returns the ExpenseSchedules context value, or null if no provider is present.
 * Use this in components that should work both inside and outside a provider
 * (e.g. reusable list/drawer components).
 */
export const useOptionalExpenseSchedulesContext = (): UseExpenseSchedulesReturn | null => {
  return useContext(ExpenseSchedulesContext);
};